import { Link } from 'react-router-dom';
import { Info, ShieldCheck } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { inlineLinkClassName } from '../../ui/link-styles';
import { StatusIcon } from '../../ui/status-icon';
import {
    ADMIN_BADGE_BASE_CLASS,
    ADMIN_BADGE_NEUTRAL_CLASS,
    ADMIN_BADGE_POSITIVE_CLASS,
} from './adminStatusBadge';
import type { CompanySsoDetailState } from './companySsoDetail';

export function AccessBadge({ hasAccess, label }: { hasAccess: boolean; label: string }) {
    return (
        <span
            className={cn(
                ADMIN_BADGE_BASE_CLASS,
                hasAccess ? ADMIN_BADGE_POSITIVE_CLASS : ADMIN_BADGE_NEUTRAL_CLASS,
                'gap-1',
            )}
        >
            {label}
            <StatusIcon value={hasAccess} className="size-3.5 text-current" />
        </span>
    );
}

export function ManageSsoButton({ ssoDetailState }: { ssoDetailState: CompanySsoDetailState }) {
    if (ssoDetailState.manageSsoDisabled || !ssoDetailState.manageSsoPath) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="inline-flex">
                            <Button type="button" variant="outline" disabled>
                                <ShieldCheck className="size-4" />
                                Manage SSO
                            </Button>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>{ssoDetailState.manageSsoTooltip}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    return (
        <Button asChild variant="outline">
            <Link to={ssoDetailState.manageSsoPath}>
                <ShieldCheck className="size-4" />
                Manage SSO
            </Link>
        </Button>
    );
}

export function AddUsersViaUsersPageNote() {
    return (
        <div className="flex gap-2.5 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-neutral-700">
            <Info className="mt-0.5 size-4 shrink-0 text-primary-500" aria-hidden />
            <p>
                To add users to this company, use the{' '}
                <Link to="/admin?tab=users" className={inlineLinkClassName}>
                    Users
                </Link>{' '}
                page.
            </p>
        </div>
    );
}

export function CompanyDetailEmptyState({ message }: { message: string }) {
    return (
        <Card>
            <CardContent className="p-8 text-sm text-neutral-600">{message}</CardContent>
        </Card>
    );
}
